import { IFormThingsToImprove } from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { selectProgramType } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { Table } from 'legacy/lut/library/list'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { IPaging, Static } from 'core-components/types'
import { Crud } from 'core/types'
import { KeyInitiative } from 'partner360/entities/keyInitiatives'
import { useRefetchPlanningCockpitData } from 'partner360/features/financialCockpit/model/hooks/useRefetchPlanningCockpitData'
import { P360Currency } from 'partner360/models/P360Currency'

import { useIsMdfActivityReadOnly } from '../../hooks/useIsMdfActivityReadOnly'
import { requestMdfActivityApply, selectMdfActivitySavingIds } from '../../model'
import { IMdfActivity, IMdfActivityWithBusinessPriorities } from '../../model/types'
import { MdfActivityDetailsModal } from '../mdfActivityDetailsModal'

import { getColumns } from './helpers/getColumns'

import styles from './MdfActivitiesTable.module.scss'

interface IMdfActivitiesTableProps {
  activities: IMdfActivity[]
  /**
   * Full, ungrouped activity list used to resolve the details-modal's selected
   * activity. Falls back to `activities` when omitted. Needed by callers (like
   * the initiatives-grouped view) that pass a filtered subset as `activities` —
   * relinking an activity to a different initiative would otherwise move it out
   * of that subset and prematurely close the modal.
   */
  allActivities?: IMdfActivity[]
  initiatives: KeyInitiative[]
  currency: P360Currency
  refetchInitiatives: () => void
  isHeaderVisible?: boolean
}

export const MdfActivitiesTable = ({
  activities,
  allActivities,
  initiatives,
  currency,
  refetchInitiatives,
  isHeaderVisible = true,
}: IMdfActivitiesTableProps) => {
  const dispatch = useAppDispatch()
  const refetchPlanningCockpitData = useRefetchPlanningCockpitData()
  const savingEntryIds = useSelector(selectMdfActivitySavingIds)
  const programType = useSelector(selectProgramType)
  const isReadOnly = useIsMdfActivityReadOnly()

  const isUsd = currency === P360Currency.USD

  const [createForActivity, setCreateForActivity] = useState<IMdfActivity | null>(null)
  const [createName, setCreateName] = useState('')
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null)
  const [focusOutcome, setFocusOutcome] = useState(false)

  const pagingRef = useRef<IPaging | undefined>(undefined)

  const recordsSource = useMemo(
    () =>
      new Crud<IMdfActivity>({
        fetcher: new Static(activities),
        keyField: 'entryId',
        defaultPaging: pagingRef.current ?? {
          page: 0,
          pageSize: activities.length,
        },
      }),
    [activities]
  )

  const initiativesSource = useMemo(
    () =>
      new Crud({
        fetcher: new Static(initiatives),
        keyField: 'EntryId',
        defaultPaging: {
          page: 0,
          pageSize: initiatives.length,
        },
      }),
    [initiatives]
  )

  const initiativesById = useMemo(() => {
    const map = new Map<number, string>()
    initiatives.forEach((initiative) => {
      const name = initiative.NameExt ?? initiative.Name ?? `Unnamed #${initiative.EntryId}`
      map.set(initiative.EntryId, name)
    })
    return map
  }, [initiatives])

  const initiativeById = useMemo(() => {
    const map = new Map<number, KeyInitiative>()
    initiatives.forEach((initiative) => map.set(initiative.EntryId, initiative))
    return map
  }, [initiatives])

  const handleLink = useCallback(
    (activity: IMdfActivity, keyInitiativeId: number | null) => {
      if (isReadOnly) return

      dispatch(requestMdfActivityApply({ activity, keyInitiativeId }))
    },
    [dispatch, isReadOnly]
  )

  const handleCreateInitiative = useCallback(
    (activity: IMdfActivity, typedValue: string) => {
      if (isReadOnly) return

      setCreateForActivity(activity)
      setCreateName(typedValue)
    },
    [isReadOnly]
  )

  const closeCreateDialog = useCallback(() => {
    setCreateForActivity(null)
    setCreateName('')
  }, [])

  const handleCreateFinished = useCallback(
    async (newData: IFormThingsToImprove[]) => {
      const savedEntryId = newData[0]?.EntryId

      if (createForActivity && savedEntryId) {
        await dispatch(
          requestMdfActivityApply({
            activity: createForActivity,
            keyInitiativeId: savedEntryId,
          })
        ).unwrap()
        refetchPlanningCockpitData()
      }

      refetchInitiatives()
      closeCreateDialog()
    },
    [closeCreateDialog, createForActivity, dispatch, refetchInitiatives, refetchPlanningCockpitData]
  )

  const handleOpenDetails = useCallback((activity: IMdfActivity) => {
    setSelectedEntryId(activity.entryId)
    setFocusOutcome(true)
  }, [])

  const columns = useMemo(
    () =>
      getColumns({
        initiativesSource,
        initiativesById,
        savingEntryIds,
        isUsd,
        isReadOnly,
        onLink: handleLink,
        onCreateInitiative: handleCreateInitiative,
        onOpenDetails: handleOpenDetails,
      }),
    [
      initiativesSource,
      initiativesById,
      savingEntryIds,
      isUsd,
      isReadOnly,
      handleLink,
      handleCreateInitiative,
      handleOpenDetails,
    ]
  )

  const selectedActivity = useMemo(() => {
    if (selectedEntryId == null) {
      return null
    }
    const found = (allActivities ?? activities).find((a) => a.entryId === selectedEntryId)
    return (found as IMdfActivityWithBusinessPriorities) ?? null
  }, [allActivities, activities, selectedEntryId])

  const selectedInitiative =
    selectedActivity?.keyInitiativeId != null
      ? (initiativeById.get(selectedActivity.keyInitiativeId) ?? null)
      : null

  return (
    <>
      <Table<IMdfActivity>
        header={isHeaderVisible}
        source={recordsSource}
        columns={columns}
        classes={{ body: styles.clickableRow }}
        onClick={(activity) => {
          setSelectedEntryId(activity.entryId)
          setFocusOutcome(false)
        }}
      />
      <MdfActivityDetailsModal
        show={selectedActivity !== null}
        activity={selectedActivity}
        initiative={selectedInitiative}
        initiativesSource={initiativesSource}
        initiativesById={initiativesById}
        focusOutcome={focusOutcome}
        onLink={handleLink}
        onCreateInitiative={handleCreateInitiative}
        onClose={() => {
          setSelectedEntryId(null)
          setFocusOutcome(false)
        }}
      />
      <KeyInitiativeEditor
        initialState={createName ? { Name: createName } : undefined}
        isDialogOpened={createForActivity !== null}
        isDataInsightPresent={programType !== ProgramTypes.Retail}
        onEditCancel={closeCreateDialog}
        onUpdateFinished={handleCreateFinished}
      />
    </>
  )
}

MdfActivitiesTable.displayName = 'MdfActivitiesTable'
